import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InfoTip } from "@/components/InfoTip";
import {
  ListChecks, Sparkles, Loader2, Camera, Plus, Trash2, Save, RefreshCw, Info, Check,
} from "lucide-react";

const TYPE_META = {
  clean: { label: "Clean", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  patch: { label: "Patch", cls: "bg-[#FEF3C7] text-[#92400E]" },
  re_photograph: { label: "Re-photograph", cls: "bg-[#E0E7FF] text-[#3730A3]" },
  ask_landlord: { label: "Ask landlord", cls: "bg-[#FCE7F3] text-[#9D174D]" },
  likely_wear: { label: "Likely ordinary wear", cls: "bg-[#F1F5F9] text-[#475569]" },
};

export function MoveOutPrepPlan({ propertyId, archived }) {
  const { isPremium } = useTier();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    api.get(`/properties/${propertyId}/prep-plan`)
      .then(({ data }) => setPlan(data.plan))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isPremium, propertyId]);

  const items = plan?.items || [];
  const doneCount = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const grouped = useMemo(() => {
    const g = {};
    for (const it of (plan?.items || [])) {
      const key = it.room_name || "My items";
      (g[key] = g[key] || []).push(it);
    }
    return g;
  }, [plan]);

  const generate = async () => {
    if (plan?.status === "saved" && !window.confirm("Regenerate the plan? This replaces your current saved plan, including completed checkmarks.")) return;
    setBusy(true); setEmpty(false);
    try {
      const { data } = await api.post(`/properties/${propertyId}/prep-plan`);
      if (data.empty) { setEmpty(true); setPlan(null); }
      else { setPlan(data.plan); toast.success("Draft prep plan ready — review and save it"); }
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "This property is archived (read-only)." : "Could not generate. Try again.");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/prep-plan/save`);
      setPlan(data.plan); toast.success("Prep plan saved");
    } catch { toast.error("Could not save plan."); } finally { setBusy(false); }
  };

  const toggle = async (item) => {
    try {
      const { data } = await api.patch(`/properties/${propertyId}/prep-plan/items/${item.item_id}`, { done: !item.done });
      setPlan(data.plan);
    } catch (e) { toast.error(e?.response?.status === 409 ? "Property is archived (read-only)." : "Could not update."); }
  };

  const remove = async (item) => {
    try { const { data } = await api.delete(`/properties/${propertyId}/prep-plan/items/${item.item_id}`); setPlan(data.plan); }
    catch { toast.error("Could not remove item."); }
  };

  const addItem = async () => {
    const area = newItem.trim();
    if (!area) return;
    try {
      const { data } = await api.post(`/properties/${propertyId}/prep-plan/items`, { area, suggestion_type: "clean" });
      setPlan(data.plan); setNewItem("");
    } catch { toast.error("Could not add item."); }
  };

  if (!isPremium) {
    return (
      <PremiumLock
        testId="prep-plan-locked"
        feature="moveout_prep"
        icon={ListChecks}
        title="Move-out prep plan"
        tip="moveoutPrep"
        description="Find things to clean, patch, re-photograph, or ask about before move-out."
      />
    );
  }

  return (
    <div id="prep-plan-card" data-testid="prep-plan-card" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <ListChecks size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Move-out prep plan</h2>
        <InfoTip topic="moveoutPrep" />
      </div>
      <p className="mt-2 text-sm text-[#475569]">Find things to clean, patch, re-photograph, or ask about before move-out.</p>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : !plan ? (
        <div className="mt-5">
          {empty ? (
            <div data-testid="prep-plan-empty" className="rounded-xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-5 text-center">
              <Camera size={22} className="mx-auto text-[#94A3B8]" />
              <p className="mt-2 text-sm font-medium text-navy">Take some move-out photos first</p>
              <p className="mt-1 text-xs text-[#64748B]">Capture your rooms, then generate a prep plan from those photos.</p>
            </div>
          ) : (
            <p className="text-sm text-[#64748B]">Generate a checklist from your most recent room photos.</p>
          )}
          <Button data-testid="prep-plan-generate-btn" disabled={busy || archived} onClick={generate}
            className="mt-4 rounded-xl h-11 bg-navy hover:bg-navy-hover text-white">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
            Generate prep plan
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span data-testid="prep-plan-status" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${plan.status === "saved" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
              {plan.status === "saved" ? <Check size={12} /> : <Sparkles size={12} />} {plan.status === "saved" ? "Saved" : "Draft — review & save"}
            </span>
            <div className="flex gap-2">
              <Button data-testid="prep-plan-regenerate-btn" variant="outline" size="sm" disabled={busy || archived}
                onClick={generate} className="rounded-lg"><RefreshCw size={14} className="mr-1.5" /> Regenerate</Button>
              {plan.status !== "saved" && (
                <Button data-testid="prep-plan-save-btn" size="sm" disabled={busy || archived}
                  onClick={save} className="rounded-lg bg-navy hover:bg-navy-hover text-white"><Save size={14} className="mr-1.5" /> Save plan</Button>
              )}
            </div>
          </div>

          {items.length > 0 && (
            <div>
              <div className="flex items-center justify-between text-xs text-[#64748B] mb-1.5">
                <span>{doneCount} of {items.length} done</span><span>{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#EEF2F6] overflow-hidden">
                <div data-testid="prep-plan-progress" className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {Object.entries(grouped).map(([room, list]) => (
            <div key={room}>
              <p className="text-sm font-semibold text-navy mb-2">{room}</p>
              <div className="space-y-2">
                {list.map((it) => {
                  const meta = TYPE_META[it.suggestion_type] || TYPE_META.clean;
                  return (
                    <div key={it.item_id} data-testid={`prep-item-${it.item_id}`}
                      className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] px-3.5 py-3">
                      <button data-testid={`prep-item-toggle-${it.item_id}`} onClick={() => toggle(it)} disabled={archived}
                        className={`mt-0.5 grid place-items-center h-5 w-5 rounded-md border shrink-0 transition-colors ${it.done ? "bg-brand border-brand text-white" : "border-[#CBD5E1] hover:border-brand"}`}>
                        {it.done && <Check size={13} />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>
                          <p className={`text-sm font-medium ${it.done ? "line-through text-[#94A3B8]" : "text-navy"}`}>{it.area?.split(" — ").slice(1).join(" — ") || it.area}</p>
                        </div>
                        {it.note && <p className="mt-1 text-xs text-[#64748B]">{it.note}</p>}
                        {it.suggestion_type === "re_photograph" && it.room_id && (
                          <button data-testid={`prep-item-rephoto-${it.item_id}`} onClick={() => navigate(`/capture/${propertyId}/${it.room_id}`)}
                            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
                            <Camera size={12} /> Re-photograph this room
                          </button>
                        )}
                      </div>
                      <button data-testid={`prep-item-delete-${it.item_id}`} onClick={() => remove(it)} disabled={archived}
                        className="text-[#CBD5E1] hover:text-red-500 shrink-0"><Trash2 size={15} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {!archived && (
            <div className="flex gap-2">
              <Input data-testid="prep-item-add-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Add your own item…" className="h-11 rounded-xl" />
              <Button data-testid="prep-item-add-btn" onClick={addItem} variant="outline" className="rounded-xl h-11"><Plus size={16} /></Button>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3 text-xs text-[#64748B]">
        <Info size={15} className="mt-0.5 shrink-0" />
        <span>TenantSeal provides organization and documentation tools, not legal advice. Suggestions are optional.</span>
      </div>
    </div>
  );
}
